import { createSlice, createSelector } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of } from "rxjs";
import { switchMap, map, catchError } from "rxjs/operators";
import { selectCustomerCountry, selectRegistrationStep } from "@nxplatform/geo-data/slices/ip-data.slice";
import { AuthProvidersList } from "@nxplatform/platform-entities";
import { CustomerFieldKeys } from "@nxplatform/platform-entities/customer/configurable-forms";
import { RegistrationTypes } from "./client/platform-settings.client.js";
const REGISTRATION_SETTINGS_FEATURE_KEY = "registrationSettings";
const initialRegistrationSettingsState = {
  cyrillicValidation: false,
  numberSymbolValidation: false,
  isBTagRequired: false,
  isBrasilRegFormRequired: false,
  isGviveCheckRequired: true,
  dobValidation: true,
  showPersonalCodeToggler: {
    active: false,
    exceptCountries: []
  },
  oneStepForm: null,
  resendTimers: null,
  baseCountry: "",
  baseCurrency: "",
  registrationFormType: RegistrationTypes.base,
  topBluemFlowEnabled: false,
  useNewDobValidation: false,
  configurableRegistration: null,
  loaded: false,
  uneditableRegistrationFields: /* @__PURE__ */ new Set(),
  postcoder: {
    active: false,
    countries: []
  },
  showHouseForCountries: [],
  selectedRegistrationProvider: null,
  registrationProgressLoading: false
};
const registrationSettingsSlice = createSlice({
  name: REGISTRATION_SETTINGS_FEATURE_KEY,
  initialState: initialRegistrationSettingsState,
  reducers: {
    getRegistrationConfigurationsStart: () => {
    },
    getRegistrationConfigurationsSuccess: (state, action) => {
      const {
        cyrillicValidation,
        numberSymbolValidation,
        isGviveCheckRequired,
        dobValidation,
        showPersonalCodeToggler,
        resendTimers,
        oneStepForm,
        postcoder,
        isBTagRequired,
        isBrasilRegFormRequired,
        topBluemFlowEnabled,
        showHouseForCountries
      } = action.payload || {};
      state.cyrillicValidation = cyrillicValidation;
      state.numberSymbolValidation = numberSymbolValidation;
      state.dobValidation = dobValidation;
      state.showPersonalCodeToggler = showPersonalCodeToggler;
      state.resendTimers = resendTimers;
      state.oneStepForm = oneStepForm;
      state.isGviveCheckRequired = isGviveCheckRequired;
      state.postcoder = postcoder;
      state.isBTagRequired = isBTagRequired;
      state.isBrasilRegFormRequired = isBrasilRegFormRequired;
      state.loaded = true;
      state.topBluemFlowEnabled = topBluemFlowEnabled;
      state.showHouseForCountries = showHouseForCountries;
    },
    getRegistrationConfigurationsFailure: () => {
    },
    registrationProviderChange: (state, action) => {
      state.selectedRegistrationProvider = action.payload;
    },
    toggleRegistrationLoadingState: (state, action) => {
      state.registrationProgressLoading = action.payload;
    },
    addUneditableRegistrationFields: (state, action) => {
      state.uneditableRegistrationFields = action.payload;
    },
    clearUneditableRegistrationFields: (state) => {
      state.uneditableRegistrationFields = initialRegistrationSettingsState.uneditableRegistrationFields;
    }
  }
});
const registrationSettingsReducer = registrationSettingsSlice.reducer;
const {
  getRegistrationConfigurationsStart,
  getRegistrationConfigurationsSuccess,
  getRegistrationConfigurationsFailure,
  registrationProviderChange,
  toggleRegistrationLoadingState,
  addUneditableRegistrationFields,
  clearUneditableRegistrationFields
} = registrationSettingsSlice.actions;
const getRegistrationSettingsState = (rootState) => rootState[REGISTRATION_SETTINGS_FEATURE_KEY];
const selectPlatformSettingsCyrillicValidationEnabled = createSelector(
  getRegistrationSettingsState,
  ({ cyrillicValidation }) => cyrillicValidation
);
const selectPlatformSettingsNumberSymbolValidationEnabled = createSelector(
  getRegistrationSettingsState,
  ({ numberSymbolValidation }) => numberSymbolValidation
);
const selectPlatformSettingRegistrationWithBtag = createSelector(
  getRegistrationSettingsState,
  ({ isBTagRequired }) => isBTagRequired
);
const selectPlatformSettingsBrasilRegFormRequired = createSelector(
  getRegistrationSettingsState,
  ({ isBrasilRegFormRequired }) => isBrasilRegFormRequired
);
const selectPlatformSettingsGviveCheckRequired = createSelector(
  getRegistrationSettingsState,
  ({ isGviveCheckRequired }) => isGviveCheckRequired
);
const selectRegistrationSettingsDobValidationEnabled = createSelector(
  getRegistrationSettingsState,
  ({ dobValidation }) => dobValidation
);
const selectRegistrationConfigLoaded = createSelector(
  getRegistrationSettingsState,
  ({ loaded }) => loaded
);
const selectPersonalCodeTogglerEnabled = createSelector(
  getRegistrationSettingsState,
  ({ showPersonalCodeToggler }) => showPersonalCodeToggler
);
const isRegistrationProgressLoading = createSelector(
  getRegistrationSettingsState,
  ({ registrationProgressLoading }) => registrationProgressLoading
);
const getConfigurableRegistrationSetup = createSelector(
  getRegistrationSettingsState,
  ({ configurableRegistration }) => configurableRegistration
);
const selectActiveCountryRegistration = createSelector(
  [getConfigurableRegistrationSetup, selectCustomerCountry],
  (regSetup = {}, customerCountry) => {
    return (regSetup == null ? void 0 : regSetup[customerCountry]) || (regSetup == null ? void 0 : regSetup["DEFAULT"]);
  }
);
const selectRegistrationProviderOptions = createSelector(
  selectActiveCountryRegistration,
  (regSetup) => {
    var _a;
    return ((_a = regSetup == null ? void 0 : regSetup.SWITCH) == null ? void 0 : _a.map((item) => item)) || [];
  }
);
const getSelectedProviderOption = createSelector(
  getRegistrationSettingsState,
  ({ selectedRegistrationProvider }) => selectedRegistrationProvider ?? AuthProvidersList.EMAIL
);
const getSelectedProviderSteps = createSelector(
  [selectActiveCountryRegistration, getSelectedProviderOption],
  (regSetup, selectedProvider) => {
    var _a, _b;
    return ((_b = (_a = regSetup == null ? void 0 : regSetup.SWITCH) == null ? void 0 : _a.find(
      (item) => item.switchProvider === selectedProvider
    )) == null ? void 0 : _b.steps) || [];
  }
);
const getSelectedProviderStepFields = createSelector([
  getSelectedProviderSteps,
  selectRegistrationStep
], (regSteps, activeStep) => {
  var _a;
  return ((_a = regSteps[activeStep]) == null ? void 0 : _a.fields) || [];
});
const getSelectedProviderFields = createSelector([
  getSelectedProviderSteps
], (regSteps) => {
  return regSteps.map((item) => item.fields) || [];
});
const selectIsWelcomBonusEnabled = createSelector(
  selectActiveCountryRegistration,
  ({ BONUSES }) => !!(BONUSES == null ? void 0 : BONUSES.length)
);
const selectUneditableRegistrationFields = createSelector(
  getRegistrationSettingsState,
  ({ uneditableRegistrationFields }) => uneditableRegistrationFields
);
const isConfigurableRegWithoutCurrencyField = createSelector(
  getSelectedProviderSteps,
  (regSteps) => !regSteps.some((step) => step.fields.some((field) => field.fieldKey === CustomerFieldKeys.currency))
);
const selectOneStepFormState = createSelector(
  getRegistrationSettingsState,
  ({ oneStepForm }) => oneStepForm
);
const selectOneStepFormStatus = createSelector(
  selectOneStepFormState,
  (oneStepForm) => oneStepForm == null ? void 0 : oneStepForm.active
);
const selectOneStepFormDisabledCountries = createSelector(
  selectOneStepFormState,
  (oneStepForm) => oneStepForm == null ? void 0 : oneStepForm.exceptCountries
);
const selectPlatformSettingsResendMailTimer = createSelector(
  getRegistrationSettingsState,
  ({ resendTimers }) => {
    const { mail } = resendTimers || {};
    return mail;
  }
);
const selectPlatformSettingsResendPhoneTimer = createSelector(
  getRegistrationSettingsState,
  ({ resendTimers }) => {
    const { phone } = resendTimers || {};
    return phone;
  }
);
const selectRegistrationBaseCountry = createSelector(
  getRegistrationSettingsState,
  ({ baseCountry }) => baseCountry
);
const selectRegistrationBaseCurrency = createSelector(
  getRegistrationSettingsState,
  ({ baseCurrency }) => baseCurrency
);
const selectRegistrationType = createSelector(
  getRegistrationSettingsState,
  ({ registrationFormType }) => registrationFormType
);
const selectPostCoderEnabled = createSelector(
  getRegistrationSettingsState,
  (s, countryCode) => countryCode,
  ({ postcoder }, countryCode) => postcoder.active && postcoder.countries.includes(countryCode)
);
const selectIsTopBluemFlowEnabled = createSelector(
  getRegistrationSettingsState,
  ({ topBluemFlowEnabled }) => topBluemFlowEnabled
);
const selectRegistrationShowHouseField = createSelector(
  getRegistrationSettingsState,
  (s, countryCode) => countryCode,
  ({ showHouseForCountries }, countryCode) => showHouseForCountries.includes(countryCode)
);
const selectIsNewDobValidation = createSelector(
  getRegistrationSettingsState,
  ({ useNewDobValidation }) => useNewDobValidation
);
const registrationConfigurationsEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getRegistrationConfigurationsStart.type),
  switchMap(() => Api.settings.registrationConfigurations().pipe(
    map((data) => getRegistrationConfigurationsSuccess(data)),
    catchError(() => of(getRegistrationConfigurationsFailure()))
  ))
);
const fetchRegistrationConfigurations = () => ({
  type: getRegistrationConfigurationsStart.type
});
export {
  REGISTRATION_SETTINGS_FEATURE_KEY,
  addUneditableRegistrationFields,
  clearUneditableRegistrationFields,
  fetchRegistrationConfigurations,
  getConfigurableRegistrationSetup,
  getRegistrationConfigurationsFailure,
  getRegistrationConfigurationsStart,
  getRegistrationConfigurationsSuccess,
  getRegistrationSettingsState,
  getSelectedProviderFields,
  getSelectedProviderOption,
  getSelectedProviderStepFields,
  getSelectedProviderSteps,
  initialRegistrationSettingsState,
  isConfigurableRegWithoutCurrencyField,
  isRegistrationProgressLoading,
  registrationConfigurationsEpic,
  registrationProviderChange,
  registrationSettingsReducer,
  registrationSettingsSlice,
  selectActiveCountryRegistration,
  selectIsNewDobValidation,
  selectIsTopBluemFlowEnabled,
  selectIsWelcomBonusEnabled,
  selectOneStepFormDisabledCountries,
  selectOneStepFormState,
  selectOneStepFormStatus,
  selectPersonalCodeTogglerEnabled,
  selectPlatformSettingRegistrationWithBtag,
  selectPlatformSettingsBrasilRegFormRequired,
  selectPlatformSettingsCyrillicValidationEnabled,
  selectPlatformSettingsGviveCheckRequired,
  selectPlatformSettingsNumberSymbolValidationEnabled,
  selectPlatformSettingsResendMailTimer,
  selectPlatformSettingsResendPhoneTimer,
  selectPostCoderEnabled,
  selectRegistrationBaseCountry,
  selectRegistrationBaseCurrency,
  selectRegistrationConfigLoaded,
  selectRegistrationProviderOptions,
  selectRegistrationSettingsDobValidationEnabled,
  selectRegistrationShowHouseField,
  selectRegistrationType,
  selectUneditableRegistrationFields,
  toggleRegistrationLoadingState
};
